import { Alert, Button, Card, Group, List, Stack, Text, ThemeIcon } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleDashed,
  IconDownload,
} from '@tabler/icons-react';
import JSZip from 'jszip';
import { useEffect, useRef, useState } from 'react';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { BULLET_STYLE_GLYPHS } from 'src/secret-menu/brand-asset-builder/lib/bulletStyles';
import { generateDocx } from 'src/secret-menu/brand-asset-builder/lib/docxModifier';
import { saveAs } from 'src/secret-menu/brand-asset-builder/lib/downloadUtils';
import { generateTitlePageArtwork } from 'src/secret-menu/brand-asset-builder/lib/imageProcessor';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Logger } from 'src/utils/Logger';
import type { BrandAssetBuilderData } from 'src/secret-menu/brand-asset-builder/types';

const EXPORT_ASSETS_TEMPLATE_URL = '/brand-asset-builder/master_template_files/export_assets.docx';
const TITLE_PAGE_TEMPLATE_URL = '/brand-asset-builder/master_template_files/title_page.docx';

type ChecklistItem = {
  label: string;
  done: boolean;
};

function ChecklistRow({ label, done }: ChecklistItem) {
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon variant="light" color={done ? 'green' : 'gray'} size={24} radius="xl">
        {done ? <IconCircleCheck size={16} /> : <IconCircleDashed size={16} />}
      </ThemeIcon>
      <Text size="textSm" c={done ? 'gray.8' : 'gray.6'}>
        {label}
      </Text>
    </Group>
  );
}

export default function GenerateStep(props: StepProps<BrandAssetBuilderData>) {
  const { data } = props;
  const [isGenerating, setIsGenerating] = useState(false);
  const [zipBlob, setZipBlob] = useState<Blob | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  // Generation never writes into the shared `data`/`setData` — its output lives entirely in this
  // component's own state, so the only staleness concern is showing UI feedback for a run the
  // admin has since navigated away from (e.g. by going "Back" mid-generation). Guarding local
  // state writes with this ref is enough; there's no shared-state race like the one T5 dealt with.
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const checklist: ChecklistItem[] = [
    { label: 'Tenant selected', done: data.company !== null },
    { label: 'Logo available', done: data.logoUrl !== null },
    { label: 'Palette approved', done: true },
    { label: 'Title page style selected', done: true },
  ];

  const handleGenerate = async () => {
    const { logoUrl } = data;
    if (!logoUrl) return;

    setIsGenerating(true);
    setGenerationError(null);
    setZipBlob(null);

    try {
      const [exportAssetsRes, titlePageRes] = await Promise.all([
        fetch(EXPORT_ASSETS_TEMPLATE_URL),
        fetch(TITLE_PAGE_TEMPLATE_URL),
      ]);

      if (!exportAssetsRes.ok) {
        throw new Error(`Failed to fetch export_assets.docx: ${exportAssetsRes.statusText}`);
      }
      if (!titlePageRes.ok) {
        throw new Error(`Failed to fetch title_page.docx: ${titlePageRes.statusText}`);
      }

      const exportAssetsBuffer = await exportAssetsRes.arrayBuffer();
      const titlePageBuffer = await titlePageRes.arrayBuffer();

      const img = new Image();
      img.src = logoUrl;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load the uploaded logo image'));
      });
      const actualLogoRatio = img.width / img.height;

      const logoBlob = await fetch(logoUrl).then(response => {
        if (!response.ok) throw new Error('Failed to fetch logo blob');
        return response.blob();
      });
      const logoArrayBuffer = await logoBlob.arrayBuffer();

      const { pngBlob: artworkPngBlob } = await generateTitlePageArtwork(
        logoBlob,
        data.titlePageTheme,
        {
          primary: data.palette.primary,
          secondary: data.palette.secondary,
          accent: data.palette.accent,
          bg: data.palette.background,
          darkText: data.palette.darkText,
        },
        data.titlePageConfig,
      );
      const artworkPngArrayBuffer = await artworkPngBlob.arrayBuffer();

      const currentDate = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      // Both documents share the same table and bullet styling so the pack reads as one brand.
      const sharedStyling = {
        clientName: data.clientName,
        currentDate,
        palette: data.palette,
        headingFont: data.headingFont,
        bodyFont: data.bodyFont,
        tableStyle: data.tableStyle,
        tableBorderColour: data.tableBorderColour,
        tableHeaderColour: data.tableHeaderColour,
        tableHeaderLighten: data.tableHeaderLighten,
        bulletColour: data.bulletColour,
        bulletGlyph: BULLET_STYLE_GLYPHS[data.bulletStyle],
      };

      const exportAssetsBlob = await generateDocx(exportAssetsBuffer, {
        ...sharedStyling,
        image1Buffer: logoArrayBuffer,
        logoRatio: actualLogoRatio,
      });

      const titlePageBlob = await generateDocx(titlePageBuffer, {
        ...sharedStyling,
        image1Buffer: artworkPngArrayBuffer,
        image2Buffer: logoArrayBuffer,
        logoRatio: actualLogoRatio,
      });

      const finalZip = new JSZip();
      finalZip.file('export_assets.docx', exportAssetsBlob);
      finalZip.file('title_page.docx', titlePageBlob);
      finalZip.file('company_logo.png', logoArrayBuffer);
      finalZip.file('title_page_image.png', artworkPngArrayBuffer);

      const blob = await finalZip.generateAsync({ type: 'blob' });

      if (isMountedRef.current) {
        setZipBlob(blob);
      }
    } catch (error) {
      Logger.error('Failed to generate the branding pack', error);
      if (isMountedRef.current) {
        setGenerationError(
          error instanceof Error
            ? error.message
            : 'Something went wrong generating the branding pack.',
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsGenerating(false);
      }
    }
  };

  const handleDownload = () => {
    if (!zipBlob) return;
    const fileName = data.clientName ? `${data.clientName}.zip` : 'Company_Branding.zip';
    saveAs(zipBlob, fileName);
  };

  let footerContent: React.ReactNode;
  if (generationError) {
    footerContent = (
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
        {generationError}
      </Alert>
    );
  } else if (zipBlob) {
    footerContent = (
      <Alert
        color="green"
        variant="light"
        icon={<IconCircleCheck size={18} />}
        title="Branding pack generated"
      >
        <Stack gap="sm">
          <Text size="textSm" c="gray.7">
            Nothing has been downloaded automatically — click below to download the branding pack.
          </Text>
          <Button
            variant="filled"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownload}
            w="fit-content"
          >
            Download branding pack
          </Button>
        </Stack>
      </Alert>
    );
  }

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Generate branding pack"
      description="Review the checklist below, then generate the branding pack for this tenant."
      continueText={isGenerating ? 'Generating…' : 'Generate'}
      showClientName={false}
      hideProgress
      allowVerticalOverflow
      isSubmitting={isGenerating}
      canGoBack={!isGenerating}
      canGoForward={!isGenerating && data.logoUrl !== null}
      nextStep={() => void handleGenerate()}
      footer={footerContent}
    >
      <Stack gap="xl">
        <Card withBorder radius="md" p="lg">
          <Stack gap="md">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
              Pre-generation checklist
            </Text>
            <Stack gap="sm">
              {checklist.map(item => (
                <ChecklistRow key={item.label} {...item} />
              ))}
            </Stack>
          </Stack>
        </Card>

        <Card withBorder radius="md" p="lg">
          <Stack gap="md">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
              Files to be generated
            </Text>
            <List spacing="xs" size="sm" c="gray.7">
              <List.Item>export_assets.docx — cloned master with patched branding</List.Item>
              <List.Item>title_page.docx — placeholder replacement and image insertion</List.Item>
              <List.Item>company_logo.png — approved tenant logo as PNG</List.Item>
              <List.Item>title_page_image.png/.jpeg — branded background artwork</List.Item>
            </List>
          </Stack>
        </Card>
      </Stack>
    </DrawerFlowScreenLayout>
  );
}
